import { createContext, useContext, useMemo, useState } from "react";
import { matched_assets, strategies_matched_assets } from "../services/CommonServices/CommonServices";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { transformData } from "../helpers/helpers";
import { convertMachedAssetsToExpertData, sortMatchedAssetsBaseLastMatchedDate } from "../helpers/epxert";
import usePermissionDenied from "../hooks/usePermissionDenied";
import { parseApiError } from "../utils/parseApiError";
import { useI18n } from "../i18n/I18nProvider";
import { isMatchedAssetsRequestReady } from "./matchedAssetsRequest";
import { shouldShowMatchedAssetsInitialLoading } from "../components/scanner/scannerLoading";

export const MatchedAssetsContext = createContext({
  data: [],
  isError: false,
  isLoading: true,
  isRefreshing: false,
  submitParams: () => {},
  params: "",
  permissionDenied: false,
  errorMessage: null,
});

/**
 * MatchedAssetsProvider
 * - Fetches matched assets (and strategy matched assets on /expert)
 * - Treats 403 as "no access" state (NOT an error toast)
 * - Exposes an optional errorMessage for non-403 errors (string, safe for existing UI)
 */
const MatchedAssetsProvider = ({ children }) => {
  const { pathname } = useLocation();
  const { t } = useI18n();

  const [request, setRequest] = useState({ params: "", pathname: "" });
  const isRequestForCurrentPath = isMatchedAssetsRequestReady({ pathname, request });

  const {
    data: rawResponse,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ["matchedAssets", request.params, pathname],
    queryFn: () => {
      // Choose the proper endpoint based on page
      if (pathname === "/expert") return strategies_matched_assets(request.params);
      return matched_assets(request.params);
    },
    // A route can mount before its NavbarPages instance has submitted that
    // route's defaults. Never issue the previous route's request in that gap.
    enabled: isRequestForCurrentPath && !!request.params,
    // Retain content during a filter refresh on the same page, but never carry
    // a previous route's result into the destination page.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey?.[2] === pathname ? keepPreviousData(previousData) : undefined,
    retry: 1,
    // Avoid repeated 403 spam on focus when access is denied
    refetchOnWindowFocus: (query) => {
      const err = query?.state?.error;
      return !(err?.response?.status === 403);
    },
  });

  // 403 is interpreted as "no access"
  const permissionDenied = usePermissionDenied(isError, error);

  // Non-403 error message (optional for UI). Keep it as string to avoid breaking other parts.
  // ✅ Policy: translation(code) → server message → common.request_failed
  const errorMessage = useMemo(() => {
    if (!isError || !error) return null;
    if (error?.response?.status === 403) return null;

    const parsed = parseApiError(error);

    const fallback = t("common.request_failed", "Request failed.");
    const serverMsg = String(parsed?.message || "").trim() || fallback;

    if (parsed?.code) {
      const key = `api_errors.${parsed.code}`;
      const candidate = t(key);
      const hasValidTranslation = candidate && candidate !== key;
      return hasValidTranslation ? candidate : serverMsg;
    }

    return serverMsg;
  }, [isError, error, t]);

  /**
   * Handles the submission of new params for the matched assets query.
   * The new params are used to update the query key and refetch the data.
   */
  const submitParams = (newParams) => {
    setRequest({ params: newParams, pathname });
  };

  // Build transformed data safely.
  // IMPORTANT: Always return an array so consuming pages do not break.
  const transformedData = useMemo(() => {
    if (!isRequestForCurrentPath || !rawResponse?.data) return [];

    if (pathname === "/expert") {
      const list = convertMachedAssetsToExpertData(rawResponse.data);
      return Array.isArray(list) ? list.sort(sortMatchedAssetsBaseLastMatchedDate) : [];
    }

    const list = transformData(rawResponse.data);
    return Array.isArray(list) ? list : [];
  }, [rawResponse, pathname, isRequestForCurrentPath]);

  const context_value = {
    data: transformedData,
    isError,
    // Only the first empty request is a blocking load; stale cards stay visible.
    isLoading: shouldShowMatchedAssetsInitialLoading({
      isRequestReady: isRequestForCurrentPath,
      params: request.params,
      queryLoading: isLoading,
      data: transformedData,
    }),
    isRefreshing:
      Boolean(isFetching) && isRequestForCurrentPath && transformedData.length > 0,
    submitParams,
    permissionDenied,
    params: isRequestForCurrentPath ? request.params : "",
    errorMessage,
  };

  return <MatchedAssetsContext.Provider value={context_value}>{children}</MatchedAssetsContext.Provider>;
};

export default MatchedAssetsProvider;

export const useMatchedAssets = () => {
  return useContext(MatchedAssetsContext);
};
